import React, { useState, useEffect, useCallback } from 'react';
import { InputGroup, Form, Button } from 'react-bootstrap';
import { FaSearch, FaTimes, FaSpinner } from 'react-icons/fa';
import { debounce } from 'lodash';

const ClinicalFormsSearch = ({ onSearch, loading, placeholder = "Search forms by name or description..." }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Debounced search function (300ms delay for real-time feel)
    const debouncedSearch = useCallback(
        debounce((term) => {
            setIsSearching(false);
            onSearch(term);
        }, 300),
        [onSearch]
    );

    useEffect(() => {
        // Only show searching indicator if search term is long enough
        if (searchTerm.trim().length >= 2) {
            setIsSearching(true);
        } else {
            setIsSearching(false);
        }
        debouncedSearch(searchTerm);
    }, [searchTerm, debouncedSearch]);

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    const handleClear = () => {
        setSearchTerm('');
        setIsSearching(false);
        onSearch('');
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
    };

    const showSpinner = isSearching || loading;

    return (
        <div className="clinical-forms-search">
            <InputGroup>
                <InputGroup.Text>
                    <FaSearch className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                    type="text"
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={handleInputChange}
                    disabled={loading}
                />
                {searchTerm && (
                    <Button
                        variant="outline-secondary"
                        onClick={handleClear}
                        disabled={loading}
                        className="clear-search-btn"
                        size="sm"
                    >
                        <FaTimes />
                    </Button>
                )}
                {showSpinner && (
                    <InputGroup.Text>
                        <FaSpinner className="fa-spin text-primary" />
                    </InputGroup.Text>
                )}
            </InputGroup>

            {searchTerm.trim().length >= 2 && (
                <div className="search-info mt-2">
                    <small className="text-muted">
                        {isSearching
                            ? `Searching for "${searchTerm}"...`
                            : `Showing results for "${searchTerm}"`
                        }
                    </small>
                </div>
            )}
            {searchTerm.trim().length === 1 && (
                <div className="search-info mt-2">
                    <small className="text-muted">
                        Type at least 2 characters to search...
                    </small>
                </div>
            )}
        </div>
    );
};

export default ClinicalFormsSearch;